<template>
  <div class="box-plot" :class="{ compact }">
    <!-- COMPACT MODE -->
    <template v-if="compact">
      <svg 
        width="100%" 
        height="24" 
        viewBox="0 0 100 24" 
        preserveAspectRatio="xMidYMid meet"
        class="compact-svg"
      >
        <!-- Whisker line (min to max) -->
        <line 
          :x1="compactX.min" y1="12" 
          :x2="compactX.max" y2="12" 
          class="compact-whisker"
        />
        
        <!-- Caps -->
        <line 
          v-for="cap in compactCaps" 
          :key="cap.key"
          :x1="cap.x" y1="6" 
          :x2="cap.x" y2="18" 
          class="compact-whisker"
        />
        
        <!-- Box (IQR) -->
        <rect 
          :x="compactX.q1" 
          y="4" 
          :width="compactX.q3 - compactX.q1" 
          height="16" 
          class="compact-box"
          rx="2"
        />
        
        <!-- Median line -->
        <line 
          :x1="compactX.median" y1="4" 
          :x2="compactX.median" y2="20" 
          class="compact-median"
        />
      </svg>
      
      <div class="compact-stats">
        <span class="compact-mean">{{ format(stats.mean) }}</span>
        <span class="compact-separator">±</span>
        <span class="compact-stdev">{{ format(stats.stdev) }}</span>
        <span v-if="unit" class="compact-unit">{{ unit }}</span>
      </div>
    </template>

    <!-- FULL MODE -->
    <template v-else>
      <div class="chart-container">
        <svg 
          width="100%" 
          height="220" 
          viewBox="0 0 280 220" 
          preserveAspectRatio="xMidYMid meet"
        >
          <g :transform="`translate(${labelOffset}, ${padding})`">
            <!-- Whiskers -->
            <line 
              v-for="whisker in whiskers" 
              :key="whisker.key"
              :x1="centerX" 
              :y1="whisker.y1" 
              :x2="centerX" 
              :y2="whisker.y2" 
              class="whisker"
            />
            
            <!-- Box (IQR) -->
            <rect 
              :x="centerX - boxWidth / 2" 
              :y="getY(stats.q3)" 
              :width="boxWidth" 
              :height="getY(stats.q1) - getY(stats.q3)" 
              class="box"
              rx="2"
            />
            
            <!-- Median line -->
            <line 
              :x1="centerX - boxWidth / 2" 
              :y1="getY(stats.median)" 
              :x2="centerX + boxWidth / 2" 
              :y2="getY(stats.median)" 
              class="median"
            />
            
            <!-- Caps -->
            <line 
              v-for="cap in caps" 
              :key="cap.key"
              :x1="centerX - capWidth / 2" 
              :y1="cap.y" 
              :x2="centerX + capWidth / 2" 
              :y2="cap.y" 
              class="whisker"
            />
            
            <!-- Labels -->
            <g v-if="showLabels">
              <text 
                v-for="label in labels" 
                :key="label.key"
                :y="label.y" 
                :class="['label', { 'label-median': label.isMedian }]"
                text-anchor="end"
                dominant-baseline="middle"
              >
                {{ label.text }}
              </text>
            </g>
          </g>
        </svg>
        
        <!-- Statistics -->
        <div v-if="showStatistics" class="stats-bar">
          <div v-for="stat in statsItems" :key="stat.label" class="stat-item">
            <span class="stat-label">{{ stat.label }}</span>
            <span class="stat-value">
              {{ stat.value }}
              <span v-if="stat.unit" class="stat-unit">{{ stat.unit }}</span>
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'BoxPlot',
  
  props: {
    stats: {
      type: Object,
      required: true,
      validator: s => ['min', 'q1', 'median', 'q3', 'max'].every(k => k in s)
    },
    boxWidth: { type: Number, default: 70 },
    capWidth: { type: Number, default: 40 },
    showLabels: { type: Boolean, default: true },
    showStatistics: { type: Boolean, default: true },
    unit: { type: String, default: '' },
    decimals: { type: Number, default: 2 },
    compact: { type: Boolean, default: false }
  },

  computed: {
    // Layout constants
    padding() { return 15 },
    height() { return 190 },
    centerX() { return 70 },
    labelOffset() { return this.showLabels ? 95 : 15 },

    range() {
      return this.stats.max - this.stats.min || 1
    },

    // Compact mode - precomputed X positions
    compactX() {
      const padding = 5
      const width = 90
      const getX = (value) => padding + ((value - this.stats.min) / this.range) * width
      
      return {
        min: getX(this.stats.min),
        q1: getX(this.stats.q1),
        median: getX(this.stats.median),
        q3: getX(this.stats.q3),
        max: getX(this.stats.max)
      }
    },

    compactCaps() {
      return [
        { key: 'min', x: this.compactX.min },
        { key: 'max', x: this.compactX.max }
      ]
    },

    // Full mode
    whiskers() {
      return [
        { key: 'top', y1: this.getY(this.stats.max), y2: this.getY(this.stats.q3) },
        { key: 'bottom', y1: this.getY(this.stats.q1), y2: this.getY(this.stats.min) }
      ]
    },

    caps() {
      return [
        { key: 'max', y: this.getY(this.stats.max) },
        { key: 'min', y: this.getY(this.stats.min) }
      ]
    },

    labels() {
      return [
        { key: 'max', y: this.getY(this.stats.max), text: `${this.$t('Max')}: ${this.format(this.stats.max)}` },
        { key: 'q3', y: this.getY(this.stats.q3), text: `Q3: ${this.format(this.stats.q3)}` },
        { key: 'median', y: this.getY(this.stats.median), text: `${this.$t('Median')}: ${this.format(this.stats.median)}`, isMedian: true },
        { key: 'q1', y: this.getY(this.stats.q1), text: `Q1: ${this.format(this.stats.q1)}` },
        { key: 'min', y: this.getY(this.stats.min), text: `${this.$t('Min')}: ${this.format(this.stats.min)}` }
      ]
    },

    statsItems() {
      const items = [
        { label: this.$t('Mean'), value: this.format(this.stats.mean), unit: this.unit },
        { label: this.$t('StdDev'), value: this.format(this.stats.stdev) }
      ]
      if (this.stats.count > 1) {
        items.push({ label: this.$t('Count'), value: this.stats.count })
      }
      return items
    }
  },

  methods: {
    getY(value) {
      const normalized = (value - this.stats.min) / this.range
      return this.height - (normalized * this.height)
    },

    format(value) {
      return value?.toFixed(this.decimals) ?? 'N/A'
    }
  }
}
</script>

<style lang="scss" scoped>
.box-plot {
  width: 100%;
}

// ================================
// COMPACT MODE
// ================================
.compact-svg {
  display: block;
}

.compact-whisker {
  stroke: $grey-6;
  stroke-width: 1;

  body.body--dark & {
    stroke: $grey-5;
  }
}

.compact-box {
  fill: rgba($primary, 0.2);
  stroke: $primary;
  stroke-width: 1.5;

  body.body--dark & {
    fill: rgba($secondary, 0.25);
    stroke: $secondary;
  }
}

.compact-median {
  stroke: $primary;
  stroke-width: 2;

  body.body--dark & {
    stroke: $secondary;
  }
}

.compact-stats {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.125rem;
  font-size: 0.625rem;
  color: $grey-7;

  body.body--dark & {
    color: $grey-5;
  }
}

.compact-mean {
  font-weight: 600;
  color: $grey-8;

  body.body--dark & {
    color: $grey-4;
  }
}

.compact-separator,
.compact-stdev,
.compact-unit {
  color: $grey-6;
}

// ================================
// FULL MODE
// ================================
.chart-container {
  background: white;
  border: 1px solid $grey-4;
  border-radius: var(--radius-md);
  padding: 1rem;

  body.body--dark & {
    background: $dark;
    border-color: $grey-8;
  }
}

.whisker {
  stroke: $grey-7;
  stroke-width: 1.5;

  body.body--dark & {
    stroke: $grey-5;
  }
}

.box {
  fill: rgba($primary, 0.15);
  stroke: $primary;
  stroke-width: 2;

  body.body--dark & {
    fill: rgba($secondary, 0.2);
    stroke: $secondary;
  }
}

.median {
  stroke: $primary;
  stroke-width: 3;

  body.body--dark & {
    stroke: $secondary;
  }
}

.label {
  font-size: 0.875rem;
  fill: $grey-7;

  body.body--dark & {
    fill: $grey-5;
  }

  &-median {
    fill: $primary;
    font-weight: 600;

    body.body--dark & {
      fill: $secondary;
    }
  }
}

.stats-bar {
  display: flex;
  gap: 1.5rem;
  background: $grey-2;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-sm);
  margin-top: 0.375rem;
  justify-content: space-around;
  flex-wrap: wrap;

  body.body--dark & {
    background: $grey-9;
  }
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-label {
  font-size: 0.6875rem;
  color: $grey-7;
  margin-bottom: 0.125rem;

  body.body--dark & {
    color: $grey-5;
  }
}

.stat-value {
  font-size: 0.8125rem;
  font-weight: 600;
  color: $grey-9;

  body.body--dark & {
    color: $grey-3;
  }
}

.stat-unit {
  font-weight: 400;
  color: $grey-7;
  font-size: 0.75rem;

  body.body--dark & {
    color: $grey-5;
  }
}

// ================================
// RESPONSIVE
// ================================
@media (max-width: 48rem), (max-height: 48rem)  {
  .stat-item {
    flex: 1 1 30%;
  }
}
</style>